import React, { useEffect, useMemo, useState } from "react";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { useSetCmpEmailSubscriberTargetMutation } from "../../../../../graphql/types";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";
import { formatCmpLastSavedLabel } from "../../../../../utils/cmpTimeZones";
import {
  buildStaffErrorFallback,
  getUserFacingErrorText,
} from "../../../../../utils/userFacingError";
import { ActionSuccessDialog } from "../../../../common/dashboard/ActionSuccessDialog";
import { SectionSaveReset } from "../common";

const targetErrorContext = getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: ["BAD_USER_INPUT"] });

export type EmailSubscriberTargetValue = {
  calendarYear: number;
  target: number;
  updatedAtUtcMilli?: number | null;
};

const formatLastSavedLabel = (target: EmailSubscriberTargetValue, timeZone: string | null | undefined) =>
  target.updatedAtUtcMilli ? formatCmpLastSavedLabel(target.updatedAtUtcMilli, timeZone) : `Current target for ${target.calendarYear}`;

const getTargetValidationError = (value: string) => {
  const trimmedValue = value.trim();

  if (!/^\d+$/.test(trimmedValue)) {
    return "Enter a whole number that is zero or greater.";
  }

  const parsedValue = Number(trimmedValue);
  if (!Number.isSafeInteger(parsedValue)) {
    return "Enter a smaller whole number.";
  }

  return null;
};

const EmailSubscriberTargetSection = ({
  chamberId,
  initialTarget,
  timeZone,
  onDirtyChange,
  onBusyChange,
}: {
  chamberId: string;
  initialTarget: EmailSubscriberTargetValue;
  /** Chamber time zone for the Last Saved stamp. */
  timeZone?: string | null;
  onDirtyChange: (isDirty: boolean) => void;
  onBusyChange?: (isBusy: boolean) => void;
}) => {
  const {
    calendarYear: initialCalendarYear,
    target: initialTargetValue,
    updatedAtUtcMilli: initialUpdatedAtUtcMilli,
  } = initialTarget;
  const [savedTarget, setSavedTarget] = useState(initialTarget);
  const [pendingInitialTarget, setPendingInitialTarget] =
    useState(initialTarget);
  const [draftTarget, setDraftTarget] = useState(String(initialTargetValue));
  const [errorText, setErrorText] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [setSubscriberTarget, { loading: saving }] =
    useSetCmpEmailSubscriberTargetMutation();

  useEffect(() => {
    setPendingInitialTarget({
      calendarYear: initialCalendarYear,
      target: initialTargetValue,
      updatedAtUtcMilli: initialUpdatedAtUtcMilli,
    });
  }, [
    initialCalendarYear,
    initialTargetValue,
    initialUpdatedAtUtcMilli,
  ]);

  const isDirty = useMemo(
    () => draftTarget.trim() !== String(savedTarget.target),
    [draftTarget, savedTarget.target]
  );

  useEffect(() => {
    if (
      isDirty ||
      (pendingInitialTarget.calendarYear === savedTarget.calendarYear &&
        pendingInitialTarget.target === savedTarget.target &&
        pendingInitialTarget.updatedAtUtcMilli ===
          savedTarget.updatedAtUtcMilli)
    ) {
      return;
    }

    setSavedTarget(pendingInitialTarget);
    setDraftTarget(String(pendingInitialTarget.target));
    setErrorText(null);
    setStatusText(null);
  }, [isDirty, pendingInitialTarget, savedTarget]);

  useEffect(() => {
    onDirtyChange(isDirty);

    return () => onDirtyChange(false);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onBusyChange?.(saving);
  }, [onBusyChange, saving]);

  useEffect(
    () => () => {
      onBusyChange?.(false);
    },
    [onBusyChange]
  );

  const handleSave = async () => {
    if (!isDirty) { setStatusText("No changes to save."); return; }
    const validationError = getTargetValidationError(draftTarget);
    if (validationError) {
      setErrorText(validationError);
      setStatusText(null);
      return;
    }

    try {
      setErrorText(null);
      setStatusText(null);
      const result = await setSubscriberTarget({
        context: targetErrorContext,
        variables: {
          input: {
            chamberId,
            calendarYear: savedTarget.calendarYear,
            target: Number(draftTarget.trim()),
          },
        },
      });
      const updatedTarget = result.data?.setCmpEmailSubscriberTarget.target;

      if (!updatedTarget) {
        throw new Error("The email subscriber target update returned no data.");
      }

      const nextTarget: EmailSubscriberTargetValue = {
        calendarYear: updatedTarget.calendarYear,
        target: updatedTarget.target,
        updatedAtUtcMilli: updatedTarget.updatedAtUtcMilli,
      };
      setSavedTarget(nextTarget);
      setPendingInitialTarget(nextTarget);
      setDraftTarget(String(nextTarget.target));
      // Design ActionSuccessModal for Save Changes (D:13295).
      setSuccessMessage("Your changes have been saved successfully!");
    } catch (error: unknown) {
      setErrorText(
        getUserFacingErrorText(
          error,
          buildStaffErrorFallback("save the email subscriber target")
        )
      );
    }
  };

  const handleReset = () => {
    setDraftTarget(String(savedTarget.target));
    setErrorText(null);
    setStatusText(null);
  };

  return (
    <section className="flex flex-col gap-[14px] text-[#0A0A0A]">
      <ActionSuccessDialog message={successMessage} onClose={() => setSuccessMessage(null)} />
      <div>
        <h2 className="text-[18px] font-semibold">Set Your Email Subscribers Yearly Target</h2>
        <p className="mt-[4px] text-[14px]">Define your annual goal for email subscribers. This target will be used to track your progress on the analytics dashboard.</p>
      </div>
      <div className="flex flex-wrap items-end justify-between gap-[24px] rounded-[12px] border border-[#ECECEC] bg-white px-[24px] py-[20px] shadow-[0_1px_2px_rgba(0,0,0,.04)]">
        <div className="flex min-w-0 flex-[1_1_420px] flex-col gap-[6px]">
          <label htmlFor="email-subscriber-yearly-target" className="text-[14px] font-semibold">Yearly Target</label>
          <p id="email-subscriber-yearly-target-description" className="text-[13px]">Enter your email subscribers goal for the selected year. This will be your target for Jan 1 – Dec 31, {savedTarget.calendarYear}</p>
          <div className="relative">
            <EmailOutlinedIcon aria-hidden="true" sx={{ width: 16, height: 16 }} className="pointer-events-none absolute left-[11px] top-1/2 -translate-y-1/2 text-[#9E9E9E]" />
            <input id="email-subscriber-yearly-target" type="number" min={0} step={1} inputMode="numeric" value={draftTarget} disabled={saving}
              onChange={event => { setDraftTarget(event.target.value); setErrorText(null); setStatusText(null); }}
              aria-describedby={`email-subscriber-yearly-target-description${errorText ? " email-subscriber-yearly-target-error" : ""}`}
              aria-invalid={Boolean(errorText)}
              className="w-full rounded-[8px] border border-[#ECECEC] bg-white py-[9px] pl-[36px] pr-[12px] text-[14px] text-[#0A0A0A] outline-none focus:border-cpaGold" />
          </div>
          {errorText ? <p id="email-subscriber-yearly-target-error" className="sr-only">{errorText}</p> : null}
        </div>
        <SectionSaveReset variant="design" layout="inline" allowUnchangedSave allowUnchangedReset isDirty={isDirty} saving={saving} lastSavedLabel={formatLastSavedLabel(savedTarget, timeZone)}
          onSave={handleSave} onReset={handleReset} statusText={statusText} errorText={errorText} />
      </div>
    </section>
  );
};

export default EmailSubscriberTargetSection;
